import { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import classes from "../homePageNew.module.css";
import { fetchReportHtml } from "../api/homePageApi";

const DEFAULT_WIDTH = 960;
const MIN_WIDTH = 520;

/**
 * Generic report preview — fetches one report's HTML on demand (only while
 * open) and shows it read-only in a side drawer, same shell as
 * RecordDrawer.js, so any module's Reporting section can reuse it for its
 * own "View report" action (the eye icon). View mode only, no edit/save —
 * deliberately not the full HTMLReportViewer used in BrandAudit's own
 * Reporting tab (an 800+ line editor tightly coupled to that module); this
 * mirrors only its real, proven pieces:
 * - content: srcDoc iframe fed by GET /llm-reporting/download/:jobId as text
 *   (HTMLReportTab.js's handleViewInline()).
 * - the report's own "Download PDF" button calls window.print() inside the
 *   iframe, which a sandboxed/srcDoc iframe can't act on directly —
 *   HTMLReportViewer.js works around this by overriding the iframe's own
 *   `print` to postMessage the parent, which pops the HTML into a real
 *   window and prints THAT. Same exact mechanism here.
 * - width: draggable via a handle on the left edge (not present on
 *   RecordDrawer, which stays fixed-width — this is scoped to just this
 *   component's own state, not the shared .drawer class).
 */
export default function ReportPreviewDrawer({ open, onClose, jobId, title, subtitle }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const htmlRef = useRef("");
  const iframeRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (!open || !jobId) return;
    let cancelled = false;
    setLoading(true);
    setHtml("");
    fetchReportHtml(jobId)
      .then((data) => {
        if (!cancelled) setHtml(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, jobId]);

  useEffect(() => {
    htmlRef.current = html;
  }, [html]);

  // Reset to the default width on every open — width is a per-session
  // adjustment, not something that should carry over into the next report
  // (same one reopened, or a different one).
  useEffect(() => {
    if (open) setWidth(DEFAULT_WIDTH);
  }, [open]);

  // Intercepts the report's own "Download PDF" button (which calls
  // window.print() inside the iframe) by relaying it to the parent window,
  // which pops the report's HTML into a real top-level window and prints
  // that — same as HTMLReportViewer.js's own print handling.
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type !== "__iframe_print_request__") return;
      const reportHtml = htmlRef.current;
      if (!reportHtml) return;
      const win = window.open("", "_blank");
      if (!win) return;
      win.document.open();
      win.document.write(reportHtml);
      win.document.close();
      win.focus();
      setTimeout(() => {
        win.print();
        win.addEventListener("afterprint", () => win.close());
        setTimeout(() => win.close(), 3000);
      }, 400);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    try {
      iframe.contentWindow.print = () => {
        window.postMessage({ type: "__iframe_print_request__" }, "*");
      };
    } catch (_) {
      // cross-origin content — nothing to patch, the report's own button
      // just won't do anything, no worse than before.
    }
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    // The iframe is a separate document with its own event context — once
    // the cursor drags back over it (shrinking the drawer), mousemove stops
    // reaching this listener at all (it goes to the iframe instead), which
    // is exactly why dragging wider worked but dragging narrower didn't.
    // Disabling pointer-events on it for the duration of the drag keeps
    // every mousemove on the parent document, regardless of where the
    // cursor is over the drawer.
    if (iframeRef.current) iframeRef.current.style.pointerEvents = "none";
    const onMouseMove = (ev) => {
      if (!draggingRef.current) return;
      // The drawer is pinned to the viewport's right edge, so its width at
      // any moment is simply "how far the cursor is from the right edge" —
      // computed fresh from the current cursor position every move, not
      // accumulated from a start-of-drag snapshot.
      const next = Math.min(Math.max(window.innerWidth - ev.clientX, MIN_WIDTH), window.innerWidth * 0.95);
      setWidth(next);
    };
    const onMouseUp = () => {
      draggingRef.current = false;
      if (iframeRef.current) iframeRef.current.style.pointerEvents = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  if (!open) return null;

  return (
    <>
      <div className={classes.drawerOverlay} onClick={onClose} />
      <div className={classes.drawer} style={{ width }}>
        <div className={classes.drawerResizeHandle} onMouseDown={handleResizeStart} title="Drag to resize" />
        <div className={classes.drawerHead}>
          <div className={classes.detailHeaderText}>
            <div className={classes.detailHeaderName}>{title || "Report"}</div>
            <div className={classes.detailHeaderSubtitle}>{subtitle || "View only"}</div>
          </div>
          <button className={classes.modalClose} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className={classes.reportPreviewBody}>
          {loading ? (
            <div className={classes.reportPreviewLoading}>
              <CircularProgress size={26} />
            </div>
          ) : (
            <iframe
              key={jobId}
              ref={iframeRef}
              srcDoc={html}
              title="Report preview"
              sandbox="allow-same-origin allow-scripts"
              className={classes.reportPreviewFrame}
              onLoad={handleIframeLoad}
            />
          )}
        </div>
      </div>
    </>
  );
}
